import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { SumOfCategories, YearMonth } from '@wilson/interfaces';
import { catchError, finalize, Observable, of } from 'rxjs';
import { SectionWrapperComponent } from '../section-wrapper/section-wrapper.component';
import { WorktimePdfTimesheetCardComponent } from '../worktime-time-pdf-timesheet-card/worktime-time-pdf-timesheet-card.component';
import { IonCol, ToastController } from '@ionic/angular';
import { WorkTimeManagementDataService } from '../../../work-time-management-data/work-time-management-data.service';
import { WorkingTimeDashboardContextService } from '../working-time-dashboard/working-time-dashboard-context.service';
import { PdfTimesheetData } from '../../../work-time-management-data/pdf-timesheet-cache.class';
import { captureException } from '@sentry/browser';

@Component({
  selector: 'wilson-working-time-pdf-timesheet',
  templateUrl: './working-time-pdf-timesheet.component.html',
  styleUrls: ['./working-time-pdf-timesheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslatePipe,
    SectionWrapperComponent,
    WorktimePdfTimesheetCardComponent,
    IonCol,
  ],
})
export class WorkingTimePdfTimesheetComponent {
  private readonly dashboardContext = inject(
    WorkingTimeDashboardContextService,
  );
  private readonly workTimeDataService = inject(WorkTimeManagementDataService);
  private readonly toastController = inject(ToastController);
  private readonly translateService = inject(TranslateService);

  protected bonuses$!: Observable<SumOfCategories[]>;
  protected readonly isTimeSheetLoadingSignal = signal(false);
  protected userTimeSheet$: Observable<PdfTimesheetData> = of({
    fileAccess: null,
    fileName: '',
  });
  private previousRefreshTrigger: number | null = null;

  constructor() {
    effect(() => {
      const trigger = this.dashboardContext.refreshTrigger();
      const isRefresh =
        this.previousRefreshTrigger !== null &&
        trigger !== this.previousRefreshTrigger;
      this.previousRefreshTrigger = trigger;
      const monthYear = this.dashboardContext.monthYear();
      this.loadUserTimeSheet(monthYear, isRefresh);
    });
  }

  private loadUserTimeSheet(monthYear: YearMonth, isRefresh = false): void {
    this.isTimeSheetLoadingSignal.set(true);
    this.userTimeSheet$ = this.workTimeDataService
      .getPdfTimesheetCache(monthYear, isRefresh)
      .pipe(
        catchError((e) => {
          this.handleError(e);
          return of({
            fileAccess: null,
            fileName: '',
          });
        }),
        finalize(() => {
          this.isTimeSheetLoadingSignal.set(false);
        }),
      );
  }

  private async handleError(e: Error): Promise<void> {
    captureException(e);
    const toast = await this.toastController.create({
      color: 'danger',
      message: this.translateService.instant('general.errorMessage'),
      duration: 3000,
      position: 'top',
    });
    await toast.present();
  }
}
